import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';


import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import {
  useGeneralSettings,
  useLiffSettings,
  useUpdateGeneralSettings,
  useUpdateLiffSettings,
} from '@/hooks/useSettings';

const schema = z.object({
  companyName: z.string().min(1, '会社名は必須です'),
  companyRepresentative: z.string(),
  companyPostalCode: z.string(),
  companyAddress: z.string(),
  companyPhone: z.string(),
  companyEmail: z.string().email('有効なメールアドレスを入力してください').or(z.literal('')),
  privacyPolicyUrl: z.string().url('有効な URL を入力してください').or(z.literal('')),
  tokushoPolicyUrl: z.string().url('有効な URL を入力してください').or(z.literal('')).nullable(),
  brandColorPrimary: z.string().regex(/^#[0-9a-fA-F]{6}$/, '#RRGGBB 形式で入力してください'),
  brandColorSecondary: z.string().regex(/^#[0-9a-fA-F]{6}$/, '#RRGGBB 形式で入力してください'),
});

type FormValues = z.infer<typeof schema>;

const defaultValues: FormValues = {
  companyName: 'caudesign',
  companyRepresentative: '',
  companyPostalCode: '',
  companyAddress: '',
  companyPhone: '',
  companyEmail: '',
  privacyPolicyUrl: '',
  tokushoPolicyUrl: '',
  brandColorPrimary: '#4a7cff',
  brandColorSecondary: '#22d3ee',
};

const LIFF_ID_PATTERN = /^\d+-[A-Za-z0-9]+$/;

export function GeneralSettingsTab() {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const { data, isLoading } = useGeneralSettings();
  const updateMutation = useUpdateGeneralSettings();

  // LIFF 設定（Phase 24f / Callable 経由保存）
  const { data: liffData, isLoading: isLiffLoading } = useLiffSettings();
  const liffMutation = useUpdateLiffSettings();
  const [liffIdInput, setLiffIdInput] = useState('');
  const [liffError, setLiffError] = useState<string | null>(null);

  useEffect(() => {
    if (liffData?.liffId) {
      setLiffIdInput(liffData.liffId);
    }
  }, [liffData?.liffId]);

  const handleLiffSave = async () => {
    const trimmed = liffIdInput.trim();
    if (!trimmed) {
      setLiffError('LIFF ID を入力してください');
      return;
    }
    if (!LIFF_ID_PATTERN.test(trimmed)) {
      setLiffError('LIFF ID の形式が不正です（例: 2009898179-G5lN0Ddp）');
      return;
    }
    setLiffError(null);
    try {
      const res = await liffMutation.mutateAsync(trimmed);
      if (res.changed) {
        toast.success('LIFF ID を保存しました');
      } else {
        toast.success('LIFF ID は既存の値と同じです（変更なし）');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`LIFF ID の保存に失敗しました: ${msg}`);
    }
  };

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    if (data) {
      form.reset({
        companyName: data.companyInfo?.name ?? data.companyName ?? '',
        companyRepresentative: data.companyInfo?.representative ?? '',
        companyPostalCode: data.companyInfo?.postalCode ?? '',
        companyAddress: data.companyInfo?.address ?? '',
        companyPhone: data.companyInfo?.phone ?? '',
        companyEmail: data.companyInfo?.email ?? '',
        privacyPolicyUrl: data.privacyPolicyUrl ?? '',
        tokushoPolicyUrl: data.tokushoPolicyUrl ?? '',
        brandColorPrimary: data.brandColorPrimary ?? '#4a7cff',
        brandColorSecondary: data.brandColorSecondary ?? '#22d3ee',
      });
    }
  }, [data, form]);

  const onSubmit = async (values: FormValues) => {
    try {
      const {
        companyRepresentative,
        companyPostalCode,
        companyAddress,
        companyPhone,
        companyEmail,
        ...rest
      } = values;
      await updateMutation.mutateAsync({
        ...rest,
        tokushoPolicyUrl: values.tokushoPolicyUrl || null,
        // 会社情報を companyInfo に集約。companyName は後方互換でミラー保存。
        companyInfo: {
          name: values.companyName.trim(),
          representative: companyRepresentative.trim(),
          postalCode: companyPostalCode.trim(),
          address: companyAddress.trim(),
          phone: companyPhone.trim(),
          email: companyEmail.trim(),
        },
      });
      toast.success('一般設定を保存しました');
    } catch (err) {
      toast.error(`保存に失敗しました: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  if (isLoading) return <Spinner />;

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="max-w-2xl space-y-6">
      <section>
        <h3 className="text-base font-bold mb-4 pb-2 border-b border-[var(--border)]">
          組織情報
        </h3>
        <div className="space-y-4">
          <div>
            <Label htmlFor="companyName">会社名</Label>
            <Input id="companyName" {...form.register('companyName')} disabled={!isOwner} />
            {form.formState.errors.companyName && (
              <p className="text-xs text-[var(--accent-red)] mt-1">
                {form.formState.errors.companyName.message}
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="companyRepresentative">代表者名</Label>
              <Input
                id="companyRepresentative"
                {...form.register('companyRepresentative')}
                disabled={!isOwner}
              />
            </div>
            <div>
              <Label htmlFor="companyPostalCode">郵便番号</Label>
              <Input
                id="companyPostalCode"
                placeholder="107-0062"
                {...form.register('companyPostalCode')}
                disabled={!isOwner}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="companyAddress">住所</Label>
            <Input
              id="companyAddress"
              placeholder="東京都港区南青山5-13-1 プレファス南青山5F"
              {...form.register('companyAddress')}
              disabled={!isOwner}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="companyPhone">電話番号</Label>
              <Input
                id="companyPhone"
                type="tel"
                {...form.register('companyPhone')}
                disabled={!isOwner}
              />
            </div>
            <div>
              <Label htmlFor="companyEmail">メールアドレス</Label>
              <Input
                id="companyEmail"
                type="email"
                {...form.register('companyEmail')}
                disabled={!isOwner}
              />
              {form.formState.errors.companyEmail && (
                <p className="text-xs text-[var(--accent-red)] mt-1">
                  {form.formState.errors.companyEmail.message}
                </p>
              )}
            </div>
          </div>
          <p className="text-xs text-[var(--text-muted)] leading-relaxed">
            ここで設定した会社情報は、請求書・見積書の発行元、メール署名フッター、発注書などで
            共通利用されます（デフォルト値）。カテゴリーごとに上書きしたい場合は
            営業・通知設定 &gt; カテゴリー管理 の各カテゴリーで設定できます。
          </p>
          <div>
            <Label htmlFor="privacyPolicyUrl">プライバシーポリシー URL</Label>
            <Input id="privacyPolicyUrl" {...form.register('privacyPolicyUrl')} disabled={!isOwner} />
          </div>
          <div>
            <Label htmlFor="tokushoPolicyUrl">特定商取引法表記 URL（任意）</Label>
            <Input
              id="tokushoPolicyUrl"
              {...form.register('tokushoPolicyUrl')}
              disabled={!isOwner}
            />
          </div>
        </div>
      </section>

      <section>
        <h3 className="text-base font-bold mb-4 pb-2 border-b border-[var(--border)]">
          ブランドカラー
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="brandColorPrimary">Primary</Label>
            <Input
              id="brandColorPrimary"
              {...form.register('brandColorPrimary')}
              disabled={!isOwner}
            />
          </div>
          <div>
            <Label htmlFor="brandColorSecondary">Secondary</Label>
            <Input
              id="brandColorSecondary"
              {...form.register('brandColorSecondary')}
              disabled={!isOwner}
            />
          </div>
        </div>
      </section>

      <p className="text-xs text-[var(--text-muted)]">
        LINE Bot の AI モデル・セッションタイムアウト設定は LINE Bot &gt; 設定タブへ移動しました（2026-07-27）。
      </p>

      {isOwner && (
        <div className="flex justify-end gap-2 pt-4 border-t border-[var(--border)]">
          <Button
            type="button"
            variant="secondary"
            onClick={() => data && form.reset()}
            disabled={updateMutation.isPending}
          >
            取り消し
          </Button>
          <Button type="submit" variant="primary" disabled={updateMutation.isPending}>
            {updateMutation.isPending ? '保存中...' : '保存'}
          </Button>
        </div>
      )}

      {/* LIFF 設定（Phase 24f / spec_line_bot.md A-12）*/}
      {/* 保存は updateLiffSettings Callable 経由（監査ログ強制）*/}
      <section className="pt-2">
        <h3 className="text-base font-bold mb-4 pb-2 border-b border-[var(--border)]">
          LIFF 設定
        </h3>
        {isLiffLoading ? (
          <Spinner />
        ) : (
          <div className="space-y-3">
            <div>
              <Label htmlFor="liffId">LIFF ID</Label>
              <Input
                id="liffId"
                placeholder="例: 2009898179-G5lN0Ddp"
                value={liffIdInput}
                onChange={(e) => {
                  setLiffIdInput(e.target.value);
                  if (liffError) setLiffError(null);
                }}
                disabled={!isOwner || liffMutation.isPending}
                autoComplete="off"
                spellCheck={false}
              />
              {liffError && (
                <p className="text-xs text-[var(--accent-red)] mt-1">{liffError}</p>
              )}
              <p className="text-xs text-[var(--text-muted)] mt-1 leading-relaxed">
                LINE Developers Console &gt; LINE ログインチャネル &gt; LIFF タブで発行された ID を貼り付けてください。
                LIFF URL は <code>https://liff.line.me/&lt;LIFF ID&gt;</code> として自動生成されます。
              </p>
              {liffData?.updatedAt && (
                <p className="text-xs text-[var(--text-muted)] mt-1">
                  最終更新:{' '}
                  {(() => {
                    const ts = liffData.updatedAt as unknown;
                    if (
                      typeof ts === 'object' &&
                      ts &&
                      'toDate' in ts &&
                      typeof (ts as { toDate: unknown }).toDate === 'function'
                    ) {
                      return (ts as { toDate: () => Date }).toDate().toLocaleString('ja-JP');
                    }
                    const d = new Date(String(ts));
                    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('ja-JP');
                  })()}
                </p>
              )}
            </div>
            {isOwner && (
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleLiffSave}
                  disabled={liffMutation.isPending || !liffIdInput.trim()}
                >
                  {liffMutation.isPending ? '保存中...' : 'LIFF ID を保存'}
                </Button>
              </div>
            )}
          </div>
        )}
      </section>
    </form>
  );
}
